/**
 * Bundle 131 P0 #5 - Event Connections tab revamp.
 *
 * Persona-reconciled layout (matchmaker + UX, docs in
 * bundle_131_routes.py):
 *
 *   Row 1: filter chips (All / Unmatched / Awaiting consent /
 *   Intro sent / Connected / Legacy hidden by default).
 *
 *   Row 2..N: full-width stacked connection cards. Each card:
 *     - Reporter name + email
 *     - Target hint first name + physical description snippet
 *     - Interest + contact preference pill (colored)
 *     - When/where prose
 *     - Reason to reconnect (truncated + expand)
 *     - Status pill
 *     - Actions: "Open matcher" -> /admin/introductions/:id
 *
 *   Legacy chip is hidden by default; opt in via the chip toggle.
 *   Empty state per filter is worded clearly.
 *
 * Hygiene: no em/en dashes, no eslint-disable.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'all',        label: 'All' },
  { key: 'unmatched',  label: 'Unmatched' },
  { key: 'awaiting',   label: 'Awaiting consent' },
  { key: 'intro_sent', label: 'Intro sent' },
  { key: 'connected',  label: 'Connected' },
];

const PILL_COLOR = {
  green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  blue:  'bg-sky-50 text-sky-800 border-sky-200',
  red:   'bg-red-50 text-red-800 border-red-200',
  grey:  'bg-neutral-100 text-neutral-700 border-neutral-200',
};

const STATUS_BADGE = {
  unmatched:  'bg-amber-50 text-amber-800 border-amber-200',
  awaiting:   'bg-sky-50 text-sky-800 border-sky-200',
  intro_sent: 'bg-primary-50 text-primary-800 border-primary-200',
  connected:  'bg-emerald-50 text-emerald-800 border-emerald-200',
  legacy:     'bg-neutral-100 text-neutral-700 border-neutral-200',
};

const EMPTY_COPY = {
  all:        'No connections captured for this event yet.',
  unmatched:  'No unmatched connections. Nice work.',
  awaiting:   'No connections awaiting consent.',
  intro_sent: 'No introductions sent yet.',
  connected:  'No connections completed yet.',
  legacy:     'No legacy connection rows on file.',
};

const ConnectionCard = ({ card }) => {
  const [expanded, setExpanded] = useState(false);
  const reporter = card.reporter || {};
  const hint = card.target_hint || {};
  const pill = card.contact_preference || {};
  const statusPill = STATUS_BADGE[card.status_bucket]
    || STATUS_BADGE.unmatched;
  const reasonTrunc = hint.reason_to_reconnect
    && hint.reason_to_reconnect.length > 120;
  return (
    <Card
      className="border-neutral-200"
      data-testid={`connection-card-${card.id}`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Reporter
            </div>
            <div className="text-primary-900 font-semibold"
                  data-testid={`connection-reporter-${card.id}`}>
              {`${reporter.first_name || ''} ${reporter.last_name || ''}`.trim()
                || 'not on file'}
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {reporter.gender ? (
                <span className="text-[10px] px-1.5 py-0 rounded border border-neutral-200 bg-neutral-50 text-neutral-700 capitalize">
                  {reporter.gender}
                </span>
              ) : null}
              {reporter.age_band ? (
                <span className="text-[10px] px-1.5 py-0 rounded border border-neutral-200 bg-neutral-50 text-neutral-700">
                  {reporter.age_band}
                </span>
              ) : null}
              {typeof reporter.prior_events_count === 'number' ? (
                <span className="text-[10px] px-1.5 py-0 rounded border border-neutral-200 bg-neutral-50 text-neutral-700"
                        data-testid={`connection-reporter-prior-${card.id}`}>
                  Prior events: {reporter.prior_events_count}
                </span>
              ) : null}
              {reporter.allow_introductions === false ? (
                <span className="text-[10px] px-1.5 py-0 rounded border border-red-200 bg-red-50 text-red-800">
                  allow_intros=NO
                </span>
              ) : null}
            </div>
            {reporter.email ? (
              <div className="text-[10px] font-mono text-neutral-500 mt-1">
                {reporter.email}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-[10px] px-2 py-0.5 rounded border capitalize ${statusPill}`}
              data-testid={`connection-status-${card.id}`}
            >
              {card.status_bucket === 'unmatched' ? 'unmatched'
                : card.status_bucket === 'awaiting' ? 'awaiting consent'
                : card.status_bucket === 'intro_sent' ? 'intro sent'
                : card.status_bucket === 'connected' ? 'connected'
                : card.status_bucket === 'legacy' ? 'legacy'
                : card.status_bucket}
            </span>
            {card.is_test ? (
              <Badge className="bg-sky-50 text-sky-800 border-sky-200 border text-[10px]">
                test
              </Badge>
            ) : null}
            {card.kind === 'direct' ? (
              <Badge variant="outline" className="text-[10px]">
                direct
              </Badge>
            ) : null}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Target hint
            </div>
            <div className="text-primary-900 font-semibold"
                  data-testid={`connection-target-${card.id}`}>
              {hint.first_name || '(no name given)'}
            </div>
            {hint.physical_description ? (
              <div className="text-xs text-neutral-700 mt-1 line-clamp-2">
                {hint.physical_description}
              </div>
            ) : null}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              Contact preference
            </div>
            <span
              className={`text-[11px] px-2 py-0.5 rounded border ${PILL_COLOR[pill.color] || PILL_COLOR.grey}`}
              data-testid={`connection-contact-pref-${card.id}`}
            >
              {pill.label || 'Not captured'}
            </span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              Interest type
            </div>
            {hint.interest_type ? (
              <span className="text-[11px] px-2 py-0.5 rounded border bg-primary-50 text-primary-800 border-primary-200 capitalize"
                    data-testid={`connection-interest-${card.id}`}>
                {hint.interest_type}
              </span>
            ) : (
              <span className="text-[11px] px-2 py-0.5 rounded border bg-neutral-100 text-neutral-700 border-neutral-200"
                    data-testid={`connection-interest-${card.id}`}>
                Not captured
              </span>
            )}
          </div>
        </div>

        {hint.when_where ? (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              When / where
            </div>
            <div className="text-sm text-primary-900">
              {hint.when_where}
            </div>
          </div>
        ) : null}

        {hint.reason_to_reconnect ? (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              Reason to reconnect
            </div>
            <div className="text-sm text-primary-900 whitespace-pre-wrap">
              {expanded || !reasonTrunc
                ? hint.reason_to_reconnect
                : `${hint.reason_to_reconnect.slice(0, 120)}.`}
              {reasonTrunc ? (
                <button
                  type="button"
                  onClick={() => setExpanded((s) => !s)}
                  className="ml-2 text-secondary-800 text-[11px]"
                  data-testid={`connection-reason-toggle-${card.id}`}
                >
                  {expanded ? (
                    <span className="inline-flex items-center">
                      <ChevronUp className="h-3 w-3 mr-0.5" /> less
                    </span>
                  ) : (
                    <span className="inline-flex items-center">
                      <ChevronDown className="h-3 w-3 mr-0.5" /> more
                    </span>
                  )}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {card.kind === 'missed' && !card.legacy ? (
          <div className="pt-1">
            <Link to={`/admin/introductions/${card.id}`}>
              <Button size="sm" variant="outline"
                        data-testid={`connection-open-matcher-${card.id}`}>
                <ExternalLink className="h-3 w-3 mr-1" />
                Open matcher
              </Button>
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const ConnectionsTabPanel = ({ eventId }) => {
  const [filterKey, setFilterKey] = useState('all');
  const [includeLegacy, setIncludeLegacy] = useState(false);
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const q = new URLSearchParams({
        filter_key: filterKey,
        include_legacy: includeLegacy ? 'true' : 'false',
      });
      const { data } = await api.get(
        `/admin/events/${eventId}/connections/tabbed?${q}`,
      );
      setPayload(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not load connections.');
    } finally { setBusy(false); }
  }, [eventId, filterKey, includeLegacy]);

  useEffect(() => { load(); }, [load]);

  const counts = payload?.counts || {};
  const cards = payload?.cards || [];
  const emptyCopy = useMemo(
    () => EMPTY_COPY[filterKey] || EMPTY_COPY.all,
    [filterKey],
  );

  return (
    <div className="space-y-3"
          data-testid="connections-tab-panel">
      <div className="flex items-center gap-2 flex-wrap"
            data-testid="connections-filter-chips">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilterKey(f.key)}
            className={
              'text-[11px] px-2.5 py-1 rounded-full border transition-colors '
              + (filterKey === f.key
                  ? 'bg-primary-800 text-white border-primary-800'
                  : 'bg-white text-primary-800 border-primary-200 hover:border-primary-400')
            }
            data-testid={`connections-chip-${f.key}`}
          >
            {f.label}
            <span className="ml-1 text-[10px] opacity-75">
              {counts[f.key] ?? 0}
            </span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setIncludeLegacy((s) => !s);
            if (!includeLegacy) setFilterKey('legacy');
            else if (filterKey === 'legacy') setFilterKey('all');
          }}
          className={
            'text-[11px] px-2.5 py-1 rounded-full border transition-colors '
            + (filterKey === 'legacy'
                ? 'bg-neutral-800 text-white border-neutral-800'
                : 'bg-neutral-50 text-neutral-700 border-neutral-200 hover:border-neutral-400')
          }
          data-testid="connections-chip-legacy"
        >
          Legacy
          <span className="ml-1 text-[10px] opacity-75">
            {counts.legacy ?? 0}
          </span>
        </button>
      </div>

      {busy ? (
        <div className="text-xs text-neutral-500"
              data-testid="connections-loading">
          Loading connections.
        </div>
      ) : cards.length === 0 ? (
        <div className="text-sm text-neutral-500 border rounded p-6 text-center"
              data-testid="connections-empty">
          {emptyCopy}
        </div>
      ) : (
        <div className="space-y-3">
          {cards.map((c) => (
            <ConnectionCard key={c.id} card={c} />
          ))}
        </div>
      )}
    </div>
  );
};
